import { useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import API from "../api/api"; // make sure path is correct

const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const pagePath = location.pathname;

  // Extract tab from query param if available
  const searchParams = new URLSearchParams(location.search);
  const tabQuery = searchParams.get("tab");
  const tabName = tabQuery || pagePath.split("/")[1] || "home";

  const handleSubmit = async () => {
    if (!message.trim()) return;

    const issue = {
      page: pagePath,
      tab: tabName,
      message,
    };

    setLoading(true);
    try {
      const response = await API.post("feedback/", issue); // POST to backend
      console.log("Feedback submitted successfully:", response.data);
      setMessage("");
      setIsOpen(false);

      toast.success("Feedback submitted successfully!");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="feedback-box"
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="bg-white dark:bg-gray-900 shadow-xl rounded-2xl w-80 p-4 border border-gray-300 dark:border-gray-700 mb-2"
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Report an Issue
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-red-500 transition"
              >
                <X size={18} />
              </button>
            </div>

            {/* Context Info */}
            <div className="mb-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 p-2 rounded-lg">
              <p className="m-0">
                <span className="font-semibold">Page:</span> {pagePath}
              </p>
              <p className="m-0">
                <span className="font-semibold">Tab:</span> {tabName}
              </p>
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the issue..."
              className="w-full h-24 p-2 border rounded-md text-sm dark:bg-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-red-500 focus:outline-none"
            />

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              disabled={loading}
              className="mt-2 w-full bg-red-600 text-white py-2 rounded-md text-sm hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Submitting..." : "Submit"}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        key="feedback-toggle"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        onClick={() => setIsOpen(!isOpen)}
        className="bg-red-600 text-white p-3 rounded-full shadow-lg hover:bg-red-700 transition"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>
    </div>
  );
};

export default FeedbackWidget;
